import { useState, useEffect } from 'react';
import { Activity, X, Clock, Zap, TrendingUp, TrendingDown, DollarSign, Target, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { tradingAPI, arbitrageAPI, binaryAPI } from '../../services/api';
import CryptoIcon from '../CryptoIcon';
import { formatPair, getCommodity } from '../../utils/commodities';

import { fmtFixed, fmtNumber } from '../../utils/format';
const EnhancedActiveOrdersView = () => {
  const [activeOrders, setActiveOrders] = useState([]);
  const [arbitrageInvestments, setArbitrageInvestments] = useState([]);
  const [binaryTrades, setBinaryTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second for smooth countdown
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchAllActive = async () => {
    try {
      setLoading(true);
      const [orders, investments, trades] = await Promise.all([
        tradingAPI.getActiveOrders().catch(() => []),
        arbitrageAPI.getActiveInvestments().catch(() => []),
        binaryAPI.getActiveTrades().catch(() => []),
      ]);
      setActiveOrders(orders);
      setArbitrageInvestments(investments);
      setBinaryTrades(trades);
    } catch (error) {
      console.error('Error fetching active items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      await tradingAPI.cancelOrder(orderId);
      toast.success('Order cancelled successfully');
      fetchAllActive();
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error('Failed to cancel order');
    }
  };

  const handleCancelInvestment = async (investmentId) => {
    try {
      await arbitrageAPI.cancelInvestment(investmentId);
      toast.success('Investment cancelled successfully');
      fetchAllActive();
    } catch (error) {
      console.error('Error cancelling investment:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel investment');
    }
  };

  useEffect(() => {
    fetchAllActive();
    // Refresh every 5 seconds
    const interval = setInterval(fetchAllActive, 5000);
    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getCountdown = (endTime) => {
    if (!endTime) return '0s';
    
    try {
      const end = new Date(endTime);
      if (isNaN(end.getTime())) return '0s';
      
      const diff = Math.max(0, Math.floor((end - currentTime) / 1000));
      
      if (diff >= 86400) {
        const days = Math.floor(diff / 86400);
        return `${days}d ${Math.floor((diff % 86400) / 3600)}h`;
      }
      if (diff >= 3600) {
        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        return `${hours}h ${minutes}m`;
      }
      if (diff >= 60) {
        const minutes = Math.floor(diff / 60);
        const seconds = diff % 60;
        return `${minutes}m ${seconds}s`;
      }
      return `${diff}s`;
    } catch {
      return '0s';
    }
  };

  // Calculate progress percentage - with robust date handling
  const getProgress = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    
    try {
      const start = new Date(startTime).getTime();
      const end = new Date(endTime).getTime();
      
      // Validate dates
      if (isNaN(start) || isNaN(end)) return 0;
      
      const now = currentTime.getTime();
      const total = end - start;
      
      // Prevent division by zero
      if (total <= 0) return 100;
      
      const elapsed = now - start;
      return Math.min(100, Math.max(0, (elapsed / total) * 100));
    } catch {
      return 0;
    }
  };

  // Calculate total portfolio value
  const calculateStats = () => {
    const totalInvested = arbitrageInvestments.reduce((sum, inv) => sum + inv.amount, 0) +
                         binaryTrades.reduce((sum, trade) => sum + trade.amount, 0);
    const totalPotentialProfit = arbitrageInvestments.reduce((sum, inv) => sum + inv.expected_return, 0) +
                                binaryTrades.reduce((sum, trade) => sum + trade.potential_profit, 0);
    const totalActive = activeOrders.length + arbitrageInvestments.length + binaryTrades.length;
    
    return { totalInvested, totalPotentialProfit, totalActive };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="glow-card p-6 rounded-xl">
        <div className="text-center py-12">
          <div className="animate-spin h-12 w-12 border-4 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-400 mt-4">Loading active items...</p>
        </div>
      </div>
    );
  }

  if (stats.totalActive === 0) {
    return (
      <div className="glow-card p-6 rounded-xl">
        <div className="text-center py-12">
          <Activity className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No active items</p>
          <p className="text-gray-500 text-sm mt-2">Your active trades, investments, and orders will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Portfolio Summary Card */}
      <div className="glow-card p-4 sm:p-6 rounded-xl bg-gradient-to-br from-purple-900/50 to-blue-900/50">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center">
          <BarChart3 className="h-5 w-5 mr-2 text-purple-400" />
          Active Portfolio Summary
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-black/30 rounded-lg p-4">
            <div className="text-xs text-gray-400 mb-1">Total Active</div>
            <div className="text-2xl font-bold text-white">{stats.totalActive}</div>
            <div className="text-xs text-purple-400 mt-1">Positions</div>
          </div>
          <div className="bg-black/30 rounded-lg p-4">
            <div className="text-xs text-gray-400 mb-1">Total Invested</div>
            <div className="text-2xl font-bold text-white">${(stats.totalInvested || 0).toLocaleString()}</div>
            <div className="text-xs text-blue-400 mt-1">USDT</div>
          </div>
          <div className="bg-black/30 rounded-lg p-4">
            <div className="text-xs text-gray-400 mb-1">Potential Profit</div>
            <div className="text-2xl font-bold text-green-400">+${(stats.totalPotentialProfit || 0).toLocaleString()}</div>
            <div className="text-xs text-green-400 mt-1">Expected</div>
          </div>
        </div>
      </div>

      {/* AI Arbitrage Investments with Progress Bars */}
      {arbitrageInvestments.length > 0 && (
        <div>
          <h4 className="text-sm font-bold text-purple-400 mb-3 px-2 flex items-center">
            <Zap className="h-4 w-4 mr-2" />
            Active AI Arbitrage Investments ({arbitrageInvestments.length})
          </h4>
          <div className="space-y-3">
            {arbitrageInvestments.map((inv) => {
              const endDate = inv.end_date || inv.end_time;
              const progress = getProgress(inv.created_at || inv.start_time, endDate) || 0;
              const countdown = getCountdown(endDate);
              const invAmount = inv.amount || 0;
              const expectedReturn = inv.expected_return || inv.expected_profit || 0;
              const totalPayout = inv.total_payout || (invAmount + expectedReturn);
              const roi = invAmount > 0 ? ((expectedReturn / invAmount) * 100).toFixed(1) : '0.0';
              const periodDays = inv.period_days || Math.round((inv.period_hours || 0) / 24) || 0;
              
              return (
                <div key={inv.id} className="glow-card p-4 rounded-xl hover:bg-gray-800/70 transition-all border-l-4 border-yellow-500">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <span className="px-3 py-1 rounded-full text-xs font-bold bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                        {(inv.package_name || 'STANDARD').toUpperCase()}
                      </span>
                      <div>
                        <div className="text-white font-bold text-lg">${invAmount.toLocaleString()}</div>
                        <div className="text-xs text-gray-500">{periodDays} day investment</div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCancelInvestment(inv.id)}
                      className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                      title="Cancel investment"
                    >
                      <X className="h-5 w-5 text-red-400" />
                    </button>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                      <span>Progress: {(progress || 0).toFixed(1)}%</span>
                      <span className="flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {countdown}
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-yellow-500 to-green-500 transition-all duration-1000 relative"
                        style={{ width: `${progress || 0}%` }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
                      </div>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-3 text-sm bg-black/20 rounded-lg p-3">
                    <div>
                      <div className="text-gray-500 text-xs mb-1 flex items-center">
                        <Target className="h-3 w-3 mr-1" />
                        ROI
                      </div>
                      <div className="text-white font-bold">{roi}%</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1 flex items-center">
                        <DollarSign className="h-3 w-3 mr-1" />
                        Profit
                      </div>
                      <div className="text-green-400 font-bold">+${fmtFixed(expectedReturn, 2)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-gray-500 text-xs mb-1">Payout</div>
                      <div className="text-yellow-400 font-bold">${fmtFixed(totalPayout, 2)}</div>
                    </div>
                  </div>

                  <div className="mt-2 text-xs text-gray-500 flex items-center justify-between">
                    <span>Started: {new Date(inv.created_at || inv.start_time).toLocaleDateString()}</span>
                    <span>Ends: {endDate ? new Date(endDate).toLocaleDateString() : 'N/A'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Binary Trades with Enhanced Visualization */}
      {binaryTrades.length > 0 && (
        <div>
          <h4 className="text-sm font-bold text-purple-400 mb-3 px-2 flex items-center">
            <TrendingUp className="h-4 w-4 mr-2" />
            Active Binary Trades ({binaryTrades.length})
          </h4>
          <div className="space-y-3">
            {binaryTrades.map((trade) => {
              const countdown = getCountdown(trade.end_time);
              const progress = getProgress(trade.start_time, trade.end_time) || 0;
              const amount = trade.amount || 0;
              const potentialProfit = trade.potential_profit || 0;
              const entryPrice = trade.entry_price || 0;
              const profitPercent = amount > 0 ? ((potentialProfit / amount) * 100).toFixed(0) : '0';
              
              return (
                <div 
                  key={trade.id} 
                  className={`glow-card p-4 rounded-xl hover:bg-gray-800/70 transition-all border-2 ${
                    trade.prediction === 'up' ? 'border-green-500/50' : 'border-red-500/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      {trade.prediction === 'up' ? (
                        <div className="p-2 bg-green-500/20 rounded-lg">
                          <TrendingUp className="h-6 w-6 text-green-400" />
                        </div>
                      ) : (
                        <div className="p-2 bg-red-500/20 rounded-lg">
                          <TrendingDown className="h-6 w-6 text-red-400" />
                        </div>
                      )}
                      <div>
                      <div className="flex items-center gap-2">
                        {getCommodity(trade.crypto_symbol) && <CryptoIcon symbol={trade.crypto_symbol} size={24} />}
                        <div className="text-white font-bold text-lg">{formatPair(trade.crypto_symbol || 'BTC')}</div>
                      </div>
                        <div className="text-xs text-gray-500">Prediction: {(trade.prediction || 'up').toUpperCase()}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-purple-400 font-bold text-xl">{countdown}</div>
                      <div className="text-xs text-gray-500">{trade.duration_seconds || 60}s trade</div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                      <span>Time Elapsed: {(progress || 0).toFixed(1)}%</span>
                      <span className="animate-pulse">● LIVE</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2.5 overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-1000 ${
                          trade.prediction === 'up' 
                            ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                            : 'bg-gradient-to-r from-red-500 to-rose-500'
                        }`}
                        style={{ width: `${progress || 0}%` }}
                      />
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-3 text-sm bg-black/20 rounded-lg p-3">
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Investment</div>
                      <div className="text-white font-bold">${fmtFixed(amount, 2)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Entry Price</div>
                      <div className="text-white font-bold">${fmtFixed(entryPrice, 2)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-gray-500 text-xs mb-1">Profit ({profitPercent}%)</div>
                      <div className="text-yellow-400 font-bold">+${fmtFixed(potentialProfit, 2)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Regular Trading Orders */}
      {activeOrders.length > 0 && (
        <div>
          <h4 className="text-sm font-bold text-purple-400 mb-3 px-2 flex items-center">
            <Activity className="h-4 w-4 mr-2" />
            Active Orders ({activeOrders.length})
          </h4>
          <div className="space-y-3">
            {activeOrders.map((order) => (
              <div key={order.id} className="glow-card p-4 rounded-xl hover:bg-gray-800/70 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      order.side === 'buy' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {(order.side || 'buy').toUpperCase()}
                    </span>
                    <span className="text-white font-semibold text-lg">{order.pair}</span>
                    <span className="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-400">
                      {(order.order_type || 'market').toUpperCase()}
                    </span>
                  </div>
                  <button
                    onClick={() => handleCancelOrder(order.id)}
                    className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                    title="Cancel order"
                  >
                    <X className="h-5 w-5 text-red-400" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500 text-xs mb-1">Amount</div>
                    <div className="text-white font-semibold">{order.amount}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs mb-1">Price</div>
                    <div className="text-white font-semibold">${order.price ? fmtNumber(order.price, 2) : 'Market'}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-gray-500 text-xs mb-1">Status</div>
                    <div className="text-yellow-400 font-semibold capitalize">{order.status}</div>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-700">
                  <div className="text-xs text-gray-500">Created: {formatDate(order.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedActiveOrdersView;